import { useTranslation } from 'react-i18next'

import { Reveal } from '@/design-system/motion'
import { Card, Skeleton } from '@/design-system/primitives'

import { useEventTeam } from '../api'
import { RubricPage } from '../components/RubricPage'

/**
 * L'équipe LeYAMO.
 *
 * Une soirée comme celle-ci tient sur des gens : cuisine, bar, accueil, régie,
 * scène. La page leur donne un visage et un nom — c'est la seule page du site
 * qui ne vend rien et n'organise rien.
 *
 * Ce ne sont **pas** les comptes de l'administration. Beaucoup de gens de
 * l'équipe n'auront jamais de compte, et plusieurs comptes n'ont rien à faire
 * ici. La liste se compose en back-office, à la main.
 */
export function TeamPage() {
  const { t } = useTranslation()

  return (
    <RubricPage title={t('team.title')} intro={t('team.intro')} introKey="site.team_intro">
      {(event) => <TeamGrid slug={event.slug} />}
    </RubricPage>
  )
}

function TeamGrid({ slug }: { slug: string }) {
  const { t } = useTranslation()
  const query = useEventTeam(slug)

  if (query.isPending) {
    return (
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {[0, 1, 2, 3, 4].map((index) => (
          <Skeleton key={index} className="h-72 w-full" />
        ))}
      </div>
    )
  }

  const members = query.data ?? []
  if (members.length === 0) {
    return <p className="max-w-xl text-content-secondary">{t('team.empty')}</p>
  }

  return (
    // Des colonnes étroites et nombreuses plutôt que trois cartes larges : une
    // fiche de personne est un portrait, elle se lit en hauteur. Trois par
    // rangée sur grand écran donnaient des cartes qui s'étalaient, avec le nom
    // perdu au milieu d'un vide horizontal.
    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {members.map((member, index) => (
        <Reveal key={`${member.name}-${member.position}`} delay={index * 0.05}>
          <Card className="flex h-full flex-col items-center px-4 py-6 text-center">
            {member.photo_url ? (
              // Portrait 3/4 : le cadre suit la forme d'un visage debout, et
              // toutes les photos se ressemblent quelles que soient celles
              // qu'on nous envoie.
              <img
                src={member.photo_url}
                alt=""
                className="aspect-[3/4] w-full rounded-md object-cover"
                loading="lazy"
              />
            ) : (
              // Pas de photo : les initiales plutôt qu'une silhouette grise,
              // qui donne à toute une équipe le même visage absent.
              <div className="flex aspect-[3/4] w-full items-center justify-center rounded-md bg-bg-overlay font-display text-heading-lg text-champagne-300">
                {member.name
                  .split(' ')
                  .map((part) => part[0] ?? '')
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
            )}

            <p className="mt-4 font-display text-heading-sm leading-tight text-content-primary">
              {member.name}
            </p>
            {member.role ? (
              <p className="overline mt-1.5 text-champagne-400">{member.role}</p>
            ) : null}
            {member.bio ? (
              <p className="mt-3 text-xs leading-relaxed text-content-secondary">{member.bio}</p>
            ) : null}
          </Card>
        </Reveal>
      ))}
    </div>
  )
}
